import { gql, StoreObject } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { FIRST_PAGE_VAR } from '../components/ChatStreamContainer';
import GET_CHAT_STREAM_ITEMS_QUERY from '../queries/GetChatStreamItems.gql';
import type {
  GetChatStreamItems,
  GetChatStreamItemsVariables,
  GetChatStreamItems_chatStreams_sequenceItems as ChatStreamSequenceItem,
} from '../queries/__generated__/GetChatStreamItems';
import { putMessage_putMessage_messages_chatMessages as PutMessageSequenceItem } from 'chat/queries/__generated__/putMessage';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';
import type {
  StreamSectionChats,
  StreamSectionChatsVariables,
} from 'chat/components/StreamSectionList/queries/__generated__/StreamSectionChats';
import { addSequenceItemsToQueryResult } from './query';
import CHAT_STREAM_PREVIEW_FRAGMENT from '../queries/ChatStreamPreviewFragment.gql';
import type { ChatStreamPreviewFragment } from '../queries/__generated__/ChatStreamPreviewFragment';
import type { SequenceItemFragment } from 'chat/components/Stream/fragments/__generated__/SequenceItemFragment';
import GET_STREAM_MESSAGES_GROUPS_QUERY from '../queries/GetStreamMessagesGroups.gql';
import type {
  GetStreamMessagesGroups,
  GetStreamMessagesGroupsVariables,
} from '../queries/__generated__/GetStreamMessagesGroups';

export function lastStreamItemSequenceNumber(streamUUID: string): string {
  let result: GetChatStreamItems | null;

  try {
    result = getApolloClientSingleton().readQuery<GetChatStreamItems, GetChatStreamItemsVariables>({
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    });
  } catch (e) {
    return '0';
  }

  return result?.chatStreams?.[0]?.sequenceItems[0]?.seq || '0';
}

export function updateChatStreamPreview(uuid: string, date: Date, preview: string) {
  const client = getApolloClientSingleton();

  client.writeFragment<ChatStreamPreviewFragment>({
    id: client.cache.identify({ __typename: 'ChatStream', uuid }),
    fragment: CHAT_STREAM_PREVIEW_FRAGMENT,
    data: {
      __typename: 'ChatStream',
      uuid,
      updatedAt: date.toISOString(),
      preview: {
        __typename: 'StreamPreview',
        preview,
        sentAt: date.toISOString(),
      },
    },
  });
}

// Broadcast a stream sequence item into all the
// stream that are connected to a specific group,
// this is useful for annoucements where message
// are written into all the chats.
export function broadcastStreamSequenceItem(
  groupUUID: string,
  groupId: number,
  item: SequenceItemFragment,
  options: {
    overrideSequenceNumber?: boolean;
  } = {}
) {
  const client = getApolloClientSingleton();

  let chatStreamsByGroupId: StreamSectionChats | null = null;

  // first, check to see if chatStreams have been
  // loaded by groupId
  try {
    chatStreamsByGroupId = client.readQuery<StreamSectionChats, StreamSectionChatsVariables>({
      query: STREAM_SECTION_CHATS_QUERY,
      variables: { groupId },
    });
  } catch (e) {
    console.log('chatStreams have not yet been loaded by group ID');
  }

  if (chatStreamsByGroupId) {
    // if chatStreams have been loaded by groupId,
    // update the Apollo store so that the chat list
    // updates with the new previews
    chatStreamsByGroupId.chatStreams?.forEach(({ uuid, type }) => {
      // don't broadcast into group convos
      if (type === 'group') {
        return;
      }
      if ('preview' in item.item) {
        // update the Apollo store so that the preview updates
        updateChatStreamPreview(uuid, new Date(), item.item.preview);
      }
    });
  }

  let cachedChatStreamsByUuid;

  // then, check to see if individual chatStreams
  // have been cached by stream uuid
  try {
    // TODO this could probably be made directly by consuming it from the cache, instead of
    // forcing it to be a client only field, it is about nthe same but more straight forward
    // https://www.apollographql.com/docs/react/local-state/managing-state-with-field-policies/
    //
    // This is defined in src/lib/apollo/cache/memory.ts
    cachedChatStreamsByUuid = getApolloClientSingleton().readQuery({
      query: gql`
        query GetCachedChatStream {
          cachedChatStreams @client {
            uuid
          }
        }
      `,
    });
    /* eslint-enable graphql/template-strings */
  } catch (e) {
    // ignore cache update if we haven't loaded any stream yet.
    return;
  }

  // if stream has been loaded, check to see if it
  // is in the group
  // @ts-expect-error ts-migrate(7031) FIXME: Binding element 'uuid' implicitly has an 'any' typ... Remove this comment to see the full error message
  cachedChatStreamsByUuid?.cachedChatStreams?.forEach(({ uuid }) => {
    const r2 = client.readQuery<GetStreamMessagesGroups, GetStreamMessagesGroupsVariables>({
      query: GET_STREAM_MESSAGES_GROUPS_QUERY,
      variables: { uuid },
    });

    // if it is in the group, add the new message
    // to the stream
    r2?.chatStreams?.forEach((stream) => {
      // don't broadcast into group convos
      if (stream.type === 'group') {
        return;
      }

      const uuids = stream.groups.map((g) => g.uuid);

      if (uuids.includes(groupUUID)) {
        addStreamSequenceItem(
          stream.uuid,
          {
            ...item,
            item: {
              ...item.item,
              // ugly type guard to make typescript understand
              ...(item.item.__typename === 'MessageItem'
                ? {
                    chat: {
                      __typename: 'ChatStream',
                      uuid: stream.uuid,
                    },
                  }
                : {}),
            },
          },
          options
        );
      }
    });
  });
}

export async function addStreamSequenceItem(
  streamUUID: string | undefined,
  item: ChatStreamSequenceItem | PutMessageSequenceItem,
  options: {
    overrideSequenceNumber?: boolean;
  } = {}
) {
  if (!streamUUID) {
    return;
  }

  const client = getApolloClientSingleton();
  let result: GetChatStreamItems | null = null;

  try {
    // let's query for chat streams, if the stream has previously been fetched
    // then  apollo will return the cached value, otherwise we need to fetch it for the first time,
    // BUT, the new message wont be there (eventual consistency) so we might as well add it,
    // if it is duplicated then memory.ts configuration should remove the duplicate
    const { data, error } = await client.query<GetChatStreamItems, GetChatStreamItemsVariables>({
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    });

    if (error) {
      throw error;
    }

    result = data;
  } catch (e) {
    console.error('addStreamSequenceItem error while querying chat streams', e);
    return;
  }

  // we don't pass in the seq from putMessage because the number
  // that api returns in the payload is incorrect, so we manually
  // calculate it here
  const message = options.overrideSequenceNumber
    ? {
        ...item,
        seq: (parseInt(lastStreamItemSequenceNumber(streamUUID), 10) + 1).toString(),
      }
    : item;

  client.writeQuery<GetChatStreamItems, GetChatStreamItemsVariables>({
    query: GET_CHAT_STREAM_ITEMS_QUERY,
    variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    // cloneDeep is needed due to an apollo problem
    data: addSequenceItemsToQueryResult(cloneDeep(result), [message]),
  });

  if ('preview' in item.item) {
    updateChatStreamPreview(streamUUID, new Date(), item.item.preview);
  }
}

export async function removeStreamSequenceItem(
  streamUUID: string | undefined,
  messageUuid: string
) {
  if (!streamUUID) {
    return;
  }

  const client = getApolloClientSingleton();

  client.cache.updateQuery<GetChatStreamItems, GetChatStreamItemsVariables>(
    {
      query: GET_CHAT_STREAM_ITEMS_QUERY,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
      overwrite: true,
    },
    (_data) => {
      const data = cloneDeep(_data);
      const chatStream = data?.chatStreams?.[0];
      if (!chatStream) {
        return data;
      }

      chatStream.sequenceItems = chatStream.sequenceItems.filter(
        (item) => item.item.uuid !== messageUuid
      );

      return data;
    }
  );
}
